import { SkyNavDrawer, cn } from "@skypointcloud/skypoint-ui";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useNavSections } from "@/hooks/use-nav-sections";
import { FOCUS_RING } from "@/lib/layout";
import { navSelectionFor } from "@/lib/routes";

const SIDEBAR_COLLAPSED_KEY = "skydata-sidebar-collapsed";
const NARROW_VIEWPORT_QUERY = "(max-width: 1024px)";

function readPersistedCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Adapter between SkyNavDrawer's `linkComponent` contract and wouter's Link.
 * Declared at module scope so its component identity is stable across renders
 * and the links keep focus and hover state instead of remounting.
 */
function WouterNavLink({
  href,
  children,
  className,
  ...rest
}: {
  href: string;
  children?: ReactNode;
  className?: string;
  [key: string]: unknown;
}) {
  return (
    // B-78 (round 9): the app's 2 px focus ring, not the browser's thin default on the dark nav.
    <Link href={href} className={cn(className, FOCUS_RING)} {...rest}>
      {children}
    </Link>
  );
}

/**
 * The left nav rail (UX-1, UX-3): flat titled sections, every row navigates
 * directly, nothing expands. Item id is the route's path, so `selectedId` is a plain
 * route comparison; a Settings tab selects the one Settings entry (`navSelectionFor`).
 *
 * An in-flow flex child of the shell rather than a fixed element, so its width
 * reflows the content column and the drawer's header row shares the content's
 * layout flow — a fixed rail and in-flow content drift by a pixel or four
 * across browsers, zoom levels and device pixel ratios, and the two header
 * dividers stop meeting.
 */
export function Sidebar() {
  const [location] = useLocation();
  const sections = useNavSections();
  const [isCollapsed, setIsCollapsed] = useState(readPersistedCollapsed);

  // Auto-collapse on narrow viewports so the rail does not crowd the content,
  // and restore the user's own desktop choice when the viewport widens. The
  // auto-collapse is deliberately NOT persisted: localStorage stays the user's
  // desktop preference.
  useEffect(() => {
    const media = window.matchMedia(NARROW_VIEWPORT_QUERY);
    const apply = () => setIsCollapsed(media.matches ? true : readPersistedCollapsed());
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  const handleCollapseChange = (next: boolean) => {
    setIsCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    } catch {
      /* private mode — collapse still works for this session */
    }
  };

  return (
    <aside id="navigation" className="relative z-40 h-full shrink-0" data-testid="sidebar">
      <SkyNavDrawer
        sections={sections}
        selectedId={navSelectionFor(location)}
        // No `appName`, `appIcon` or `appHref`: the brand lockup shows once, in the top bar
        // (owner, 2026-09-28, "Two skyData"), so the drawer's header holds only the collapse
        // toggle, expanded and collapsed. Without a name or an icon the drawer renders no
        // header link, so the collapsed rail has no anonymous link to name either.
        collapsible
        collapsed={isCollapsed}
        onCollapseChange={handleCollapseChange}
        size="medium"
        // The shell is desktop-first and binds the content column to this
        // drawer's width, so the library's mobile overlay mode is off.
        mobileBreakpoint={0}
        linkComponent={WouterNavLink}
        className="h-full"
        testId="sidebar-nav-drawer"
      />
    </aside>
  );
}
